import { useEffect, useMemo, useState } from "react";
import { FileUp, Loader2, ScanLine, Ruler, AlertTriangle, CheckCircle2, Layers3, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const badgeClass = (state) => {
  if (state === "GEOMETRY_AMBIGUOUS") return "bg-amber-100 text-amber-800 border-amber-200";
  if (state === "CANDIDATE") return "bg-sky-100 text-sky-800 border-sky-200";
  if (state === "PROJECT_VERIFIED") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

export default function Takeoff() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [drawingId, setDrawingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedViewport, setSelectedViewport] = useState(null);
  const [localGeometry, setLocalGeometry] = useState(null);
  const [geometryLoading, setGeometryLoading] = useState(false);
  const [netRegion, setNetRegion] = useState(null);
  const [resolverLoading, setResolverLoading] = useState(false);
  const [correctionMode, setCorrectionMode] = useState(false);
  const [correctionPoints, setCorrectionPoints] = useState([]);
  const [confirmed, setConfirmed] = useState({});
  const [linking, setLinking] = useState(false);
  const [edgeOverrides, setEdgeOverrides] = useState({});
  const [workItemQuery, setWorkItemQuery] = useState("");
  const [workItems, setWorkItems] = useState([]);
  const [workItemsLoading, setWorkItemsLoading] = useState(false);
  const [volumeWorkItemId, setVolumeWorkItemId] = useState("");
  const [formworkWorkItemId, setFormworkWorkItemId] = useState("");
  const [scopeOffers, setScopeOffers] = useState([]);
  const [scopeOfferId, setScopeOfferId] = useState("");
  const [scopeGaps, setScopeGaps] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [heightSupportFile, setHeightSupportFile] = useState(null);
  const [heightSupportLoading, setHeightSupportLoading] = useState(false);
  const [crossHeightEvidence, setCrossHeightEvidence] = useState(null);
  const [heightDrafts, setHeightDrafts] = useState({});
  const [heightReviewLoading, setHeightReviewLoading] = useState("");
  const [detailSupportFile, setDetailSupportFile] = useState(null);
  const [detailSupportLoading, setDetailSupportLoading] = useState(false);
  const [detailHeightEvidence, setDetailHeightEvidence] = useState(null);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const page = analysis?.pages?.[0] || null;
  const viewports = page?.viewport_candidates || [];
  const displayedWallTakeoff =
    detailHeightEvidence?.pages?.find((p) => p.page === page?.page)?.wall_linear_takeoff
    || crossHeightEvidence?.pages?.find((p) => p.page === page?.page)?.wall_linear_takeoff
    || page?.wall_linear_takeoff
    || [];

  useEffect(() => {
    if (!page?.vertical_assembly_count || scopeOffers.length) return;
    api.listOffers().then((rows) => {
      setScopeOffers(Array.isArray(rows) ? rows : []);
    }).catch(() => {});
  }, [page?.vertical_assembly_count, scopeOffers.length]);
  const regionsForViewport = useMemo(() => {
    if (!selectedViewport) return [];
    const rows = page?.scored_regions || [];
    return rows.filter((r) => r.viewport_id === selectedViewport.id);
  }, [page, selectedViewport]);

  const reviewedFormwork = useMemo(() => {
    const edges = netRegion?.formwork?.edge_audit || [];
    if (!edges.length) return { edges: [], total: Number(netRegion?.formwork?.total_area_m2 || 0) };
    const reviewed = edges.map((edge) => {
      const classification = edgeOverrides[edge.edge_id] || edge.classification;
      const include = classification === "FREE_EDGE" || classification === "STEP_P40_P60" || classification === "STEP_THICKNESS_CHANGE";
      return {
        ...edge,
        classification,
        formwork_area_m2: include ? Number(edge.length_m || 0) * Number(edge.height_m || 0) : 0,
        source: edgeOverrides[edge.edge_id] ? "HUMAN_CORRECTED" : edge.source,
      };
    });
    return {
      edges: reviewed,
      total: reviewed.reduce((sum, edge) => sum + Number(edge.formwork_area_m2 || 0), 0),
    };
  }, [netRegion, edgeOverrides]);

  const reviewHeightSegment = async (row, segment, action) => {
    if (!drawingId) return;
    setHeightReviewLoading(segment.segment_id);
    try {
      const payload = {
        drawing_id: drawingId,
        code: row.code,
        segment_id: segment.segment_id,
        action,
        candidate_height_m: segment.candidate_height_m || null,
        height_m: action === "OVERRIDE_HEIGHT" ? Number(heightDrafts[segment.segment_id] || 0) : null,
        evidence_id: crossHeightEvidence?.evidence?.id || null,
        source_note: action === "OVERRIDE_HEIGHT" ? "Override tecnico da revisione /takeoff" : "Revisione altezza candidata da /takeoff",
      };
      if (action === "OVERRIDE_HEIGHT" && !(payload.height_m > 0)) {
        toast.error("Inserisci un'altezza valida.");
        return;
      }
      await api.takeoffReviewHeight(payload);
      if (heightSupportFile) {
        const refreshed = await api.takeoffResolveCrossDrawingHeights(drawingId, heightSupportFile);
        setCrossHeightEvidence(refreshed);
      }
      toast.success(
        action === "REJECT_CANDIDATE"
          ? "Altezza candidata rifiutata."
          : "Altezza confermata e area aggiornata."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revisione altezza non riuscita.");
    } finally {
      setHeightReviewLoading("");
    }
  };

  const resolveDetailHeights = async () => {
    if (!drawingId || !detailSupportFile) {
      toast.error("Seleziona un dettaglio/sezione PDF.");
      return;
    }
    setDetailSupportLoading(true);
    try {
      const result = await api.takeoffResolveDetailHeights(drawingId, detailSupportFile);
      setDetailHeightEvidence(result);
      const n = Number(result?.evidence?.explicit_evidence_count || 0);
      const resolved = Number(result?.evidence?.resolved_segments || 0);
      toast.success(`Dettaglio: ${n} evidenze esplicite, ${resolved} segmenti risolti.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Risoluzione dettaglio non riuscita.");
    } finally {
      setDetailSupportLoading(false);
    }
  };

  const resolveCrossDrawingHeights = async () => {
    if (!drawingId || !heightSupportFile) {
      toast.error("Seleziona una pianta architettonica di supporto.");
      return;
    }
    setHeightSupportLoading(true);
    try {
      const result = await api.takeoffResolveCrossDrawingHeights(drawingId, heightSupportFile);
      setCrossHeightEvidence(result);
      const candidates = Number(result?.evidence?.candidate_segments || 0);
      const resolved = Number(result?.evidence?.resolved_segments || 0);
      const total = Number(result?.evidence?.total_segments || 0);
      toast.success(`Cross-drawing: ${candidates} candidati, ${resolved} verificati su ${total} segmenti.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Risoluzione altezze cross-drawing non riuscita.");
    } finally {
      setHeightSupportLoading(false);
    }
  };

  const reconcileScope = async () => {
    if (!drawingId || !scopeOfferId) {
      toast.error("Seleziona l'offerta da confrontare.");
      return;
    }
    setScopeLoading(true);
    try {
      const result = await api.takeoffScopeGaps(drawingId, scopeOfferId);
      setScopeGaps(result);
      if (result.missing_count > 0) {
        toast.warning(`${result.missing_count} pacchetti progettuali senza corrispondenza CME.`);
      } else {
        toast.success("Tutti i pacchetti progettuali risultano coperti.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Confronto Scope Gap non riuscito.");
    } finally {
      setScopeLoading(false);
    }
  };

  const searchWorkItems = async (query = workItemQuery) => {
    setWorkItemsLoading(true);
    try {
      const rows = await api.takeoffSearchWorkItems(query);
      setWorkItems(rows || []);
      if (!volumeWorkItemId) {
        const volume = (rows || []).find((x) => String(x.unit || "").toLowerCase().includes("m3") || String(x.unit || "").toLowerCase().includes("m³"));
        if (volume) setVolumeWorkItemId(volume.id);
      }
      if (!formworkWorkItemId) {
        const formwork = (rows || []).find((x) => String(x.unit || "").toLowerCase().includes("m2") || String(x.unit || "").toLowerCase().includes("m²"));
        if (formwork) setFormworkWorkItemId(formwork.id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ricerca voci computo non riuscita.");
    } finally {
      setWorkItemsLoading(false);
    }
  };

  const chooseFile = (event) => {
    const next = event.target.files?.[0];
    if (!next) return;
    if (next.type !== "application/pdf" && !next.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seleziona un file PDF.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(next);
    setPreviewUrl(URL.createObjectURL(next));
    setAnalysis(null);
    setDrawingId("");
    setSelectedViewport(null);
    setLocalGeometry(null);
    setNetRegion(null);
    setCorrectionMode(false);
    setCorrectionPoints([]);
    setConfirmed({});
    setEdgeOverrides({});
    setWorkItemQuery("");
    setWorkItems([]);
    setVolumeWorkItemId("");
    setFormworkWorkItemId("");
    setScopeOfferId("");
    setScopeGaps(null);
    setHeightSupportFile(null);
    setCrossHeightEvidence(null);
    setDetailSupportFile(null);
    setDetailHeightEvidence(null);
    setProgress(0);
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true);
    setProgress(0);
    try {
      const result = await api.takeoffAnalyzeDrawing(file, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      setDrawingId(result.drawing?.id || "");
      setAnalysis(result.analysis || null);
      const first = result.analysis?.pages?.[0]?.viewport_candidates?.[0] || null;
      setSelectedViewport(first);
      toast.success("Tavola analizzata.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analisi tavola non riuscita.");
    } finally {
      setLoading(false);
    }
  };
  const resolveViewport = async (viewport, splitSegment = null) => {
    if (!drawingId || !viewport) return;
    const resolverType = viewport.resolver_profile?.resolver_type;
    if (!resolverType || resolverType === "REVIEW_REQUIRED") return;
    setResolverLoading(true);
    try {
      const result = await api.takeoffResolveNetRegion(
        drawingId,
        viewport.id,
        splitSegment ? { split_segment: splitSegment } : {}
      );
      setNetRegion(result);
      setEdgeOverrides({});
      setCorrectionPoints([]);
    } catch (err) {
      setNetRegion(null);
      const detail = err?.response?.data?.detail;
      if (detail && detail !== "Seed P40/P60 non presenti nella viewport") {
        toast.error(typeof detail === "string" ? detail : "Net Region Resolver non disponibile.");
      }
    } finally {
      setResolverLoading(false);
    }
  };

  const loadViewportGeometry = async (viewport) => {
    if (!drawingId || !viewport) return;
    setSelectedViewport(viewport);
    setGeometryLoading(true);
    setNetRegion(null);
    setCorrectionMode(false);
    setCorrectionPoints([]);
    try {
      const result = await api.takeoffViewportGeometry(drawingId, viewport.id, 120);
      setLocalGeometry(result.geometry || null);
      await resolveViewport(viewport);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geometria locale non disponibile.");
    } finally {
      setGeometryLoading(false);
    }
  };

  const confirmNetRegion = async () => {
    if (!netRegion || !selectedViewport) return;
    if (netRegion.quantity_status && netRegion.quantity_status !== "READY") {
      toast.error("La quantità non è ancora promuovibile: completa scala e verifiche richieste.");
      return;
    }
    if ((netRegion.regions || []).some((r) => r.area_real_m2 == null || r.perimeter_real_m == null)) {
      toast.error("Scala geometrica necessaria prima della conferma.");
      return;
    }
    try {
      for (const region of netRegion.regions || []) {
        const saved = await api.takeoffCreateRegion({
          work_item_id: volumeWorkItemId || `UNLINKED:${drawingId}`,
          drawing_id: drawingId,
          viewport_id: selectedViewport.id,
          region_class: region.region_class,
          region_key: region.region_key || null,
          polygon: region.polygon,
          holes: [],
          area_real_m2: region.area_real_m2,
          perimeter_real_m: region.perimeter_real_m,
          thickness_m: region.thickness_m,
          geometry_confidence: netRegion.split?.confidence_score || (netRegion.resolver_type === "SINGLE_REGION" ? 0.9 : 0),
          semantic_confidence: selectedViewport.semantic_confidence || 0,
          authority_level: "C",
          validation_state: netRegion.split?.source === "HUMAN_CORRECTED" ? "HUMAN_CORRECTED" : "AI_PROPOSED",
          source: netRegion.split?.source || netRegion.resolver_type || "TAKEOFF_RESOLVER",
        });
        await api.takeoffReviewRegion(saved.id, { action: "CONFIRM", reason: "Conferma grafica UI /takeoff" });
      }
      if (netRegion.formwork?.total_area_m2 != null) {
        await api.takeoffReviewFormwork({
          drawing_id: drawingId,
          viewport_id: selectedViewport.id,
          work_item_code: workItems.find((x) => x.id === formworkWorkItemId)?.code || "UNLINKED",
          edges: reviewedFormwork.edges,
          calculated_area_m2: Number(netRegion.formwork.total_area_m2),
          reviewed_area_m2: Number(reviewedFormwork.total || 0),
          reason: Object.keys(edgeOverrides).length ? "Classificazione bordi corretta manualmente in UI /takeoff" : "Classificazione bordi confermata in UI /takeoff",
        });
      }
      const reviewedResult = {
        ...netRegion,
        formwork: {
          ...netRegion.formwork,
          edge_audit: reviewedFormwork.edges,
          reviewed_area_m2: Number(reviewedFormwork.total || 0),
          total_area_m2: Number(reviewedFormwork.total || 0),
        },
      };
      setConfirmed((prev) => ({ ...prev, [selectedViewport.id]: reviewedResult }));
      toast.success("Regioni e casseri confermati e salvati.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Salvataggio regioni non riuscito.");
    }
  };

  const linkBenchmarks = async () => {
    const rows = Object.values(confirmed);
    if (!rows.length) return;
    if (!volumeWorkItemId && !formworkWorkItemId) {
      toast.error("Seleziona almeno una voce di computo da collegare.");
      return;
    }
    const volume = rows.reduce((s, r) => s + Number(r.volume_m3 || 0), 0);
    const formwork = rows.reduce((s, r) => s + Number(r.formwork?.total_area_m2 || 0), 0);
    setLinking(true);
    try {
      if (volumeWorkItemId) {
        const item = workItems.find((x) => x.id === volumeWorkItemId);
        await api.takeoffLinkWorkItem({ item_id: volumeWorkItemId, takeoff_quantity: volume, boq_quantity: item?.quantity ?? null, unit: item?.unit || "m3", drawing_id: drawingId, source_type: "NET_REGION_VOLUME", note: "Quantità volume verificata da /takeoff" });
      }
      if (formworkWorkItemId) {
        const item = workItems.find((x) => x.id === formworkWorkItemId);
        await api.takeoffLinkWorkItem({ item_id: formworkWorkItemId, takeoff_quantity: formwork, boq_quantity: item?.quantity ?? null, unit: item?.unit || "m2", drawing_id: drawingId, source_type: "FORMWORK_EDGES_AND_STEP", note: "Casseri verificati da /takeoff" });
      }
      toast.success("Quantità Takeoff collegate alle voci di computo selezionate.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Collegamento al computo non riuscito.");
    } finally {
      setLinking(false);
    }
  };

  const handleCorrectionPoint = async (point) => {
    if (!correctionMode || !selectedViewport) return;
    const next = [...correctionPoints, point].slice(-2);
    setCorrectionPoints(next);
    if (next.length === 2) {
      setCorrectionMode(false);
      await resolveViewport(selectedViewport, [next[0][0], next[0][1], next[1][0], next[1][1]]);
    }
  };

  return (
    <div className="space-y-6" data-testid="takeoff-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold">Drawing Intelligence</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">Computo & Takeoff</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Analizza tavole PDF vettoriali, individua viewport, regioni e spessori, poi verifica le quantità prima di usarle nell'offerta.
          </p>
        </div>
        <label className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-3 text-sm font-semibold cursor-pointer hover:bg-slate-800">
          <FileUp className="w-4 h-4" />
          Carica tavola PDF
          <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={chooseFile} />
        </label>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(360px,0.9fr)] gap-6">
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden min-h-[680px]">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-slate-900">{file?.name || "Nessuna tavola selezionata"}</div>
              {page && <div className="text-xs text-slate-500 mt-0.5">{page.segment_count?.toLocaleString("it-IT")} segmenti · {page.shx_annotation_count} SHX · {page.viewport_count} viewport</div>}
            </div>
            <button onClick={analyze} disabled={!file || loading} className="inline-flex items-center gap-2 rounded-lg bg-orange-600 disabled:bg-slate-300 text-white px-3 py-2 text-sm font-semibold hover:bg-orange-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              {loading ? "Analisi " + progress + "%" : "Analizza"}
            </button>
          </div>
          {previewUrl ? (
            <iframe title="Anteprima tavola" src={previewUrl} className="w-full h-[calc(100vh-245px)] min-h-[620px] bg-slate-100" />
          ) : (
            <div className="min-h-[620px] flex items-center justify-center text-center p-8 bg-slate-50">
              <div>
                <Layers3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <div className="font-semibold text-slate-600">Carica una tavola strutturale PDF</div>
                <div className="text-sm text-slate-400 mt-1">Il PDF resta visibile qui mentre il motore estrae geometrie e annotazioni.</div>
              </div>
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Ruler className="w-4 h-4 text-orange-600" />
              <h2 className="font-semibold text-slate-900">Stato analisi</h2>
            </div>
            {!page ? <p className="text-sm text-slate-500">Analizza una tavola per vedere i risultati.</p> : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat label="Segmenti" value={page.segment_count?.toLocaleString("it-IT")} />
                <Stat label="Annotazioni SHX" value={page.shx_annotation_count} />
                <Stat label="Poligoni" value={page.polygon_count} />
                <Stat label="Viewport" value={page.viewport_count} />
                {(page.declared_scales || []).length > 0 && <Stat label="Scala dichiarata" value={(page.declared_scales || []).map((x) => x.ratio).join(", ")} />}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900">Viewport riconosciute</h2>
              {geometryLoading && <Loader2 className="w-4 h-4 animate-spin text-orange-600" />}
            </div>
            <div className="space-y-2 max-h-72 overflow-auto">
              {viewports.map((v) => (
                <button key={v.id} onClick={() => loadViewportGeometry(v)}
                  className={"w-full text-left rounded-xl border px-3 py-2.5 transition " + (selectedViewport?.id === v.id ? "border-orange-300 bg-orange-50" : "border-slate-200 hover:bg-slate-50")}>
                  <div className="text-xs font-mono text-slate-400">{v.id} · {v.type}</div>
                  <div className="text-sm font-semibold text-slate-800 mt-0.5">{v.title}</div>
                  <div className="text-xs text-slate-500 mt-1">Confidence {Math.round((v.semantic_confidence || 0) * 100)}% · {v.resolver_profile?.resolver_type || "REVIEW_REQUIRED"}</div>
                  {(v.resolver_profile?.thicknesses_m || []).length > 0 && (
                    <div className="text-[11px] text-slate-400 mt-0.5">Spessori: {(v.resolver_profile.thicknesses_m || []).map((x) => Math.round(Number(x) * 100) + " cm").join(" · ")}</div>
                  )}
                </button>
              ))}
              {!viewports.length && <div className="text-sm text-slate-400">Nessuna viewport disponibile.</div>}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <h2 className="font-semibold text-slate-900 mb-3">Regioni proposte</h2>
            <div className="space-y-2">
              {regionsForViewport.map((r, idx) => (
                <div key={r.region_class + "-" + idx} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-900">{r.region_class}</div>
                      <div className="text-xs text-slate-500">{r.source_text}</div>
                    </div>
                    <span className={"text-[10px] px-2 py-1 rounded-full border font-semibold " + badgeClass(r.selection_state)}>{r.selection_state}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div>Spessore: <b>{r.thickness_m ? r.thickness_m.toFixed(2) + " m" : "—"}</b></div>
                    <div>Score: <b>{r.region_score ?? "—"}</b></div>
                    <div>Poligono: <b>{r.polygon_candidate_id || "—"}</b></div>
                    <div>Viewport: <b>{r.viewport_type || "—"}</b></div>
                  </div>
                </div>
              ))}
              {selectedViewport && !regionsForViewport.length && <div className="text-sm text-slate-400">Nessuna regione proposta per questa viewport.</div>}
            </div>
          </div>
          {displayedWallTakeoff.length > 0 && (
            <div className="rounded-2xl border border-cyan-200 bg-white shadow-sm p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-cyan-700 font-semibold">Linear Wall Takeoff</div>
                  <h2 className="font-semibold text-slate-900 mt-0.5">Murature colorate da pianta</h2>
                </div>
                <span className="text-[10px] px-2 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-semibold">
                  {page.wall_linear_takeoff_count} tipi
                </span>
              </div>

              <div className="mb-3 rounded-xl border border-violet-100 bg-violet-50/60 p-3">
                <div className="text-[11px] font-semibold text-violet-900 mb-1">Detail / Section Height Resolver</div>
                <div className="text-[11px] text-violet-800 mb-2">
                  Promuove automaticamente un'altezza solo quando nello stesso dettaglio compaiono codice ME.xx, contesto e quota H esplicita.
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => {
                      setDetailSupportFile(e.target.files?.[0] || null);
                      setDetailHeightEvidence(null);
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-violet-200 bg-white px-2 py-1.5 text-xs"
                  />
                  <button
                    onClick={resolveDetailHeights}
                    disabled={!detailSupportFile || detailSupportLoading}
                    className="rounded-lg bg-violet-700 text-white px-3 py-2 text-xs font-semibold disabled:bg-slate-300 hover:bg-violet-800"
                  >
                    {detailSupportLoading ? "Analisi..." : "Applica dettaglio"}
                  </button>
                </div>
                {detailHeightEvidence?.evidence && (
                  <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px]">
                    <div className="rounded bg-white border border-violet-100 p-1.5">
                      <div className="text-slate-400">Evidenze esplicite</div>
                      <div className="font-semibold text-violet-700">{detailHeightEvidence.evidence.explicit_evidence_count || 0}</div>
                    </div>
                    <div className="rounded bg-white border border-violet-100 p-1.5">
                      <div className="text-slate-400">Segmenti risolti</div>
                      <div className="font-semibold text-emerald-700">{detailHeightEvidence.evidence.resolved_segments || 0}</div>
                    </div>
                    <div className="rounded bg-white border border-violet-100 p-1.5">
                      <div className="text-slate-400">Area verificata</div>
                      <div className="font-semibold text-emerald-700">{Number(detailHeightEvidence.evidence.resolved_area_m2 || 0).toFixed(2)} m²</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-3 rounded-xl border border-cyan-100 bg-cyan-50/60 p-3">
                <div className="text-[11px] font-semibold text-cyan-900 mb-1">Cross-Drawing Height Resolver</div>
                <div className="text-[11px] text-cyan-800 mb-2">
                  Carica la pianta architettonica dello stesso livello: l'Abaco dei Locali viene usato come fonte di altezza tramite codice vano.
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => {
                      setHeightSupportFile(e.target.files?.[0] || null);
                      setCrossHeightEvidence(null);
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-cyan-200 bg-white px-2 py-1.5 text-xs"
                  />
                  <button
                    onClick={resolveCrossDrawingHeights}
                    disabled={!heightSupportFile || heightSupportLoading}
                    className="rounded-lg bg-cyan-700 text-white px-3 py-2 text-xs font-semibold disabled:bg-slate-300 hover:bg-cyan-800"
                  >
                    {heightSupportLoading ? "Risoluzione..." : "Risolvi altezze"}
                  </button>
                </div>
                {crossHeightEvidence?.evidence && (
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-1.5 text-[10px]">
                    <div className="rounded bg-white border border-cyan-100 p-1.5">
                      <div className="text-slate-400">Fonte</div>
                      <div className="font-semibold text-slate-700 truncate">{crossHeightEvidence.evidence.filename}</div>
                    </div>
                    <div className="rounded bg-white border border-cyan-100 p-1.5">
                      <div className="text-slate-400">Vani letti</div>
                      <div className="font-semibold text-slate-700">{crossHeightEvidence.evidence.room_count}</div>
                    </div>
                    <div className="rounded bg-white border border-cyan-100 p-1.5">
                      <div className="text-slate-400">Candidati</div>
                      <div className="font-semibold text-amber-700">{crossHeightEvidence.evidence.candidate_segments || 0}</div>
                    </div>
                    <div className="rounded bg-white border border-cyan-100 p-1.5">
                      <div className="text-slate-400">Area candidata</div>
                      <div className="font-semibold text-amber-700">{Number(crossHeightEvidence.evidence.candidate_area_m2 || 0).toFixed(2)} m²</div>
                    </div>
                    <div className="rounded bg-white border border-cyan-100 p-1.5">
                      <div className="text-slate-400">Area verificata</div>
                      <div className="font-semibold text-emerald-700">{Number(crossHeightEvidence.evidence.resolved_area_m2 || 0).toFixed(2)} m²</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {displayedWallTakeoff.map((row) => (
                  <div key={row.code} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-900">{row.code}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {row.declared_scale} · {row.plan_tag_count} tag · {row.segment_count} segmenti
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-cyan-800">{Number(row.length_m || 0).toFixed(2)} m</div>
                        <div className="text-[10px] text-slate-400">spessore grafico ≈ {Number(row.reference_strip_cm_real || 0).toFixed(1)} cm</div>
                      </div>
                    </div>
                    {!!row.context_summary?.length && (
                      <div className="mt-2 grid grid-cols-2 gap-1.5">
                        {row.context_summary.map((ctx) => (
                          <div key={ctx.context} className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5 text-[11px]">
                            <div className="flex justify-between gap-2">
                              <span className="font-semibold text-slate-700">{ctx.context}</span>
                              <span className="text-slate-500">{Number(ctx.length_m || 0).toFixed(2)} m</span>
                            </div>
                            {Number(ctx.resolved_area_m2 || 0) > 0 && (
                              <div className="text-emerald-700 mt-0.5">
                                area con altezza sorgente: {Number(ctx.resolved_area_m2).toFixed(2)} m²
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {Number(row.resolved_segment_count || 0) > 0 ? (
                      <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-[11px] text-emerald-900">
                        {row.resolved_segment_count} segmenti hanno un'altezza esplicita; area verificata: {Number(row.resolved_area_m2 || 0).toFixed(2)} m². Gli altri restano esclusi dal totale verificato.
                      </div>
                    ) : Number(row.candidate_segment_count || 0) > 0 ? (
                      <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 text-[11px] text-amber-900">
                        {row.candidate_segment_count} segmenti hanno altezza candidata dall'Abaco dei Locali; area candidata: {Number(row.candidate_area_m2 || 0).toFixed(2)} m². Richiede sezione/dettaglio o conferma tecnica prima dell'uso economico.
                      </div>
                    ) : (
                      <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 text-[11px] text-amber-900">
                        Metri lineari verificati. Per trasformarli in m² serve l’altezza documentata del tratto.
                      </div>
                    )}

                    {!!row.segments?.some((s) => s.height_status === "HEIGHT_CANDIDATE") && (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/50 p-2">
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-amber-800 mb-2">
                          Revisione altezze candidate
                        </div>
                        <div className="space-y-2 max-h-56 overflow-auto">
                          {row.segments.filter((s) => s.height_status === "HEIGHT_CANDIDATE").map((segment) => (
                            <div key={segment.segment_id} className="rounded-lg border border-amber-100 bg-white p-2 text-[11px]">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-mono font-semibold text-slate-700">{segment.segment_id}</div>
                                  <div className="text-slate-500">
                                    {Number(segment.length_m || 0).toFixed(2)} m · {segment.context || "UNCLASSIFIED"}
                                  </div>
                                  <div className="text-amber-700 mt-0.5">
                                    candidato H={Number(segment.candidate_height_m || 0).toFixed(2)} m
                                    {segment.height_evidence?.[0]?.room_code ? " · vano " + segment.height_evidence[0].room_code : ""}
                                  </div>
                                </div>
                                <div className="font-semibold text-amber-800">
                                  {Number(segment.candidate_area_m2 || 0).toFixed(2)} m²
                                </div>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                <button
                                  onClick={() => reviewHeightSegment(row, segment, "CONFIRM_CANDIDATE")}
                                  disabled={heightReviewLoading === segment.segment_id}
                                  className="rounded bg-emerald-600 px-2 py-1 text-white font-semibold disabled:bg-slate-300"
                                >
                                  Conferma candidato
                                </button>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  placeholder="H override m"
                                  value={heightDrafts[segment.segment_id] || ""}
                                  onChange={(e) => setHeightDrafts((prev) => ({ ...prev, [segment.segment_id]: e.target.value }))}
                                  className="w-24 rounded border border-slate-200 px-2 py-1"
                                />
                                <button
                                  onClick={() => reviewHeightSegment(row, segment, "OVERRIDE_HEIGHT")}
                                  disabled={heightReviewLoading === segment.segment_id}
                                  className="rounded bg-slate-700 px-2 py-1 text-white font-semibold disabled:bg-slate-300"
                                >
                                  Usa override
                                </button>
                                <button
                                  onClick={() => reviewHeightSegment(row, segment, "REJECT_CANDIDATE")}
                                  disabled={heightReviewLoading === segment.segment_id}
                                  className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700 font-semibold disabled:opacity-50"
                                >
                                  Rifiuta
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {page?.vertical_assembly_count > 0 && (
            <div className="rounded-2xl border border-fuchsia-200 bg-white shadow-sm p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-fuchsia-600 font-semibold">Scope Gap Intelligence</div>
                  <h2 className="font-semibold text-slate-900 mt-0.5">Pacchetti verticali da abaco</h2>
                </div>
                <span className="text-[10px] px-2 py-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 font-semibold">
                  {page.vertical_assembly_count} pacchetti
                </span>
              </div>

              <div className="flex gap-2 mb-3">
                <select
                  value={scopeOfferId}
                  onChange={(e) => { setScopeOfferId(e.target.value); setScopeGaps(null); }}
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Seleziona offerta/CME...</option>
                  {(scopeOffers || []).map((offer) => (
                    <option key={offer.id} value={offer.id}>
                      {offer.title || offer.client || offer.id}
                    </option>
                  ))}
                </select>
                <button
                  onClick={reconcileScope}
                  disabled={!scopeOfferId || scopeLoading}
                  className="rounded-lg bg-fuchsia-600 text-white px-3 py-2 text-sm font-semibold disabled:bg-slate-300 hover:bg-fuchsia-700"
                >
                  {scopeLoading ? "Confronto..." : "Confronta CME"}
                </button>
              </div>

              {scopeGaps && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <Stat label="Pacchetti" value={scopeGaps.assembly_count} />
                  <Stat label="Coperti" value={scopeGaps.covered_count} />
                  <Stat label="Scoperti" value={scopeGaps.missing_count} />
                </div>
              )}

              <div className="space-y-2 max-h-[28rem] overflow-auto">
                {(scopeGaps?.assemblies || page.vertical_assemblies || []).map((assembly) => {
                  const status = assembly.scope_status || "NON_CONFRONTATO";
                  const statusClass = status === "MISSING_SCOPE"
                    ? "bg-red-50 text-red-700 border-red-200"
                    : status === "COVERED_BY_PA_ITEM"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : status === "COVERED"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-600 border-slate-200";
                  return (
                    <div key={assembly.code} className="rounded-xl border border-slate-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900">{assembly.code}</div>
                          <div className="text-xs text-slate-600 mt-0.5">{assembly.title || assembly.description || "Pacchetto verticale"}</div>
                          {assembly.application && <div className="text-[11px] text-slate-400 mt-0.5">{assembly.application}</div>}
                        </div>
                        <span className={"shrink-0 text-[10px] px-2 py-1 rounded-full border font-semibold " + statusClass}>{status}</span>
                      </div>
                      {!!assembly.layers?.length && (
                        <div className="mt-2 text-[11px] text-slate-500">
                          {(assembly.layers || []).slice(0, 6).join(" · ")}
                        </div>
                      )}
                      {!!assembly.numeric_hints_cm?.length && (
                        <div className="mt-1 text-[10px] text-slate-400">
                          Quote/spessori letti: {assembly.numeric_hints_cm.join(" · ")} cm
                        </div>
                      )}
                      {!!assembly.cme_matches?.length && (
                        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-100 p-2 text-[11px] text-slate-600">
                          CME: {assembly.cme_matches.map((m) => (m.code || "Senza codice") + " · " + (m.description || "")).join(" | ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {scopeGaps?.missing_count > 0 && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-900">
                  I pacchetti in rosso sono presenti negli elaborati ma non hanno una corrispondenza esatta nel CME selezionato: devono essere verificati, quantificati e prezzati prima dell'offerta.
                </div>
              )}
            </div>
          )}

          {page?.footing_summary?.family_count > 0 && (
            <div className="rounded-2xl border border-indigo-200 bg-white shadow-sm p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-indigo-600 font-semibold">Foundation Legend Intelligence</div>
                  <h2 className="font-semibold text-slate-900 mt-0.5">Plinti: legenda ↔ pianta</h2>
                </div>
                <span className={
                  "text-[10px] px-2 py-1 rounded-full border font-semibold " +
                  (page.footing_summary.reconciliation_status === "PASS"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : page.footing_summary.reconciliation_status === "MISMATCH"
                      ? "bg-red-50 text-red-700 border-red-200"
                      : "bg-amber-50 text-amber-700 border-amber-200")
                }>
                  {page.footing_summary.reconciliation_status || "REVIEW_REQUIRED"}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <Stat label="Plinti legenda" value={page.footing_summary.total_units} />
                <Stat label="Contati in pianta" value={page.footing_summary.plan_occurrence_total ?? "—"} />
                <Stat label="Volume" value={Number(page.footing_summary.total_volume_m3 || 0).toFixed(2) + " m³"} />
                <Stat label="Casseri teorici" value={Number(page.footing_summary.total_formwork_m2 || 0).toFixed(2) + " m²"} />
              </div>
              <div className="max-h-64 overflow-auto rounded-xl border border-slate-200">
                {(page.footing_summary.families || []).map((family) => (
                  <div key={family.family_id} className="grid grid-cols-[52px_minmax(110px,1fr)_56px_56px_72px] gap-2 px-3 py-2 border-b border-slate-100 text-xs items-center">
                    <div>
                      <div className="font-mono font-semibold text-indigo-700">{family.family_id}</div>
                      <div className="text-[10px] text-slate-400">{family.plan_symbol || "—"}</div>
                    </div>
                    <div className="text-slate-600">
                      {(family.components || []).map((c) => c.dimensions_cm.join("×")).join(" + ")} cm
                    </div>
                    <div className="text-right text-slate-500" title="Quantità dichiarata in legenda">
                      L {family.quantity}
                    </div>
                    <div className="text-right text-slate-700" title="Occorrenze contate in pianta">
                      P {family.plan_count ?? "—"}
                    </div>
                    <div className={
                      "text-right font-semibold " +
                      (family.count_status === "MATCH"
                        ? "text-emerald-700"
                        : family.count_status === "MISMATCH"
                          ? "text-red-700"
                          : "text-amber-700")
                    }>
                      {family.count_status === "MATCH"
                        ? "OK"
                        : family.count_status === "MISMATCH"
                          ? ((family.count_difference > 0 ? "+" : "") + family.count_difference)
                          : "verifica"}
                    </div>
                  </div>
                ))}
              </div>
              {(page.footing_summary.mismatch_family_count > 0 || page.footing_summary.unresolved_family_count > 0) && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  La legenda non è completamente riconciliata con la pianta:
                  {" "}{page.footing_summary.mismatch_family_count || 0} famiglie discordanti,
                  {" "}{page.footing_summary.unresolved_family_count || 0} da verificare.
                  Le quantità restano utilizzabili come proposta tecnica, non come verifica automatica definitiva.
                </div>
              )}
              <button
                onClick={() => setConfirmed((prev) => ({
                  ...prev,
                  ["footing-" + drawingId]: {
                    volume_m3: Number(page.footing_summary.total_volume_m3 || 0),
                    formwork: { total_area_m2: Number(page.footing_summary.total_formwork_m2 || 0) },
                    source: page.footing_summary.reconciliation_status === "PASS"
                      ? "FOUNDATION_LEGEND_RECONCILED"
                      : "FOUNDATION_LEGEND_REVIEW_REQUIRED",
                    reconciliation_status: page.footing_summary.reconciliation_status,
                  },
                }))}
                className="mt-3 w-full rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"
              >
                {page.footing_summary.reconciliation_status === "PASS"
                  ? "Usa quantità riconciliate"
                  : "Usa quantità con revisione richiesta"}
              </button>
              <div className="text-[11px] text-slate-400 mt-2">
                Volume: dimensioni × quantità della legenda. Conteggio pianta: etichette Tipo/P1 indipendenti dalla legenda. I casseri restano una superficie teorica dei fianchi soggetta a verifica tecnica.
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold text-slate-900">Geometria locale</h2>
              {selectedViewport && <button onClick={() => loadViewportGeometry(selectedViewport)} className="p-1.5 rounded-lg hover:bg-slate-100" title="Ricalcola"><RefreshCw className="w-4 h-4 text-slate-500" /></button>}
            </div>
            {!localGeometry ? <p className="text-sm text-slate-500 mt-2">Seleziona una viewport per isolare i poligoni locali.</p> : (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Poligoni locali" value={localGeometry.polygon_count} />
                  <Stat label="Padding" value={localGeometry.padding + " PDF"} />
                </div>
                <div className="space-y-1.5 max-h-44 overflow-auto">
                  {(localGeometry.polygon_candidates || []).slice(0, 8).map((p) => (
                    <div key={p.id} className="flex justify-between gap-3 text-xs border-b border-slate-100 pb-1.5">
                      <span className="font-mono text-slate-500">{p.id}</span>
                      <span className="text-slate-700">{Math.round(p.area_pdf2).toLocaleString("it-IT")} u²</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-semibold text-slate-900">Region Resolver</h2>
              {resolverLoading && <Loader2 className="w-4 h-4 animate-spin text-orange-600" />}
            </div>
            {!netRegion ? (
              <p className="text-sm text-slate-500">
                Seleziona una viewport con spessori riconosciuti. Il motore sceglie automaticamente SINGLE_REGION, TWO_SEED_SPLIT o MULTI_CELL.
              </p>
            ) : (
              <div className="space-y-3">
                <NetRegionSvg
                  result={netRegion}
                  edges={reviewedFormwork.edges}
                  correctionMode={correctionMode}
                  correctionPoints={correctionPoints}
                  onPoint={handleCorrectionPoint}
                />
                <div className="grid grid-cols-2 gap-2">
                  {(netRegion.regions || []).map((r) => (
                    <Stat
                      key={r.region_key || r.region_class}
                      label={(r.region_key || ("T" + Math.round(Number(r.thickness_m || 0) * 100)))}
                      value={(r.area_real_m2 != null ? Number(r.area_real_m2).toFixed(2) + " m²" : Math.round(Number(r.area_pdf2 || 0)).toLocaleString("it-IT") + " u² · scala richiesta") + " · sp. " + Math.round(Number(r.thickness_m || 0) * 100) + " cm"}
                    />
                  ))}
                  <Stat label="Volume" value={netRegion.volume_m3 != null ? Number(netRegion.volume_m3).toFixed(3) + " m³" : "— · scala richiesta"} />
                  <Stat label="Casseri rivisti" value={netRegion.formwork?.total_area_m2 != null ? Number(reviewedFormwork.total || 0).toFixed(2) + " m²" : "— · topologia da verificare"} />
                </div>
                {netRegion.resolver_type === "MULTI_CELL" && (
                  <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs text-sky-950">
                    <div className="font-semibold">Multi-Cell coverage {Math.round(Number(netRegion.coverage || 0) * 100)}%</div>
                    <div className="mt-1">
                      Seed risolti {netRegion.resolved_seed_count || 0}/{netRegion.seed_count || 0}
                      {" · "}celle {netRegion.regions?.length || 0}
                      {" · "}proposte griglia {netRegion.proposed_seed_count || 0}
                      {" · "}conflitti {netRegion.conflicts?.length || 0}
                      {" · "}non risolti {netRegion.unresolved_seeds?.length || 0}
                    </div>
                    <div className="mt-1 font-semibold">
                      Stato quantità: {netRegion.quantity_status || "REVIEW_REQUIRED"}
                      {netRegion.assisted_coverage != null && <> · copertura assistita {Math.round(Number(netRegion.assisted_coverage || 0) * 100)}%</>}
                    </div>
                    {netRegion.quantity_status === "SCALE_REQUIRED" && (
                      <div className="mt-1">La geometria è analizzata, ma m²/m³ restano bloccati finché non viene calibrata la scala.</div>
                    )}
                  </div>
                )}
                {(reviewedFormwork.edges || []).length > 0 && (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700">
                    Audit bordi
                  </div>
                  <div className="max-h-64 overflow-auto">
                    {(reviewedFormwork.edges || []).map((edge) => (
                      <div key={edge.edge_id} className="grid grid-cols-[92px_1fr_76px] gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs">
                        <div>
                          <div className="font-mono font-semibold text-slate-700">{edge.edge_id}</div>
                          <div className="text-slate-400">{Number(edge.length_m || 0).toFixed(2)} m × {Number(edge.height_m || 0).toFixed(2)} m</div>
                          {(edge.review_flags || []).map((flag) => (
                            <div key={flag} className="mt-0.5 text-[10px] font-semibold text-amber-700">
                              {flag === "NEAR_EXISTING_REVIEW" ? "Vicino a esistente — verifica" : "Micro-bordo — verifica"}
                            </div>
                          ))}
                        </div>
                        {edge.classification === "STEP_P40_P60" || edge.classification === "STEP_THICKNESS_CHANGE" ? (
                          <span className="text-violet-700 font-semibold">Gradino spessori</span>
                        ) : edge.classification === "INTERNAL_SPLIT" ? (
                          <span className="text-slate-400">Interno — escluso</span>
                        ) : (
                          <select
                            value={edge.classification}
                            onChange={(e) => setEdgeOverrides((prev) => ({ ...prev, [edge.edge_id]: e.target.value }))}
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
                          >
                            <option value="FREE_EDGE">Cassero</option>
                            <option value="EXISTING_CONTACT">Contro esistente</option>
                            <option value="EXCLUDED">Escludi</option>
                          </select>
                        )}
                        <div className="text-right font-semibold text-slate-800">{Number(edge.formwork_area_m2 || 0).toFixed(2)} m²</div>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 py-2 bg-slate-50 text-xs flex justify-between">
                    <span>Calcolo automatico: <b>{Number(netRegion.formwork?.total_area_m2 || 0).toFixed(2)} m²</b></span>
                    <span>Revisionato: <b>{Number(reviewedFormwork.total || 0).toFixed(2)} m²</b></span>
                  </div>
                </div>
                )}
                <div className="text-xs text-slate-500">
                  {netRegion.split
                    ? <>Split confidence {Math.round((netRegion.split?.confidence_score || 0) * 100)}% · supporto {netRegion.split?.repeat_count || 1} · scala {netRegion.scale_source}</>
                    : <>{netRegion.resolver_type || "RESOLVER"} · scala {netRegion.scale_source} · {netRegion.quantity_status || "READY"}</>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={confirmNetRegion}
                    disabled={(netRegion.quantity_status && netRegion.quantity_status !== "READY") || (netRegion.regions || []).some((r) => r.area_real_m2 == null)}
                    className="rounded-lg bg-emerald-600 disabled:bg-slate-300 text-white px-3 py-2 text-xs font-semibold hover:bg-emerald-700">
                    CONFERMA
                  </button>
                  {netRegion.split && (
                    <button
                      onClick={() => { setCorrectionMode(true); setCorrectionPoints([]); }}
                      className="rounded-lg bg-amber-100 text-amber-900 px-3 py-2 text-xs font-semibold hover:bg-amber-200"
                    >
                      CORREGGI GRAFICAMENTE
                    </button>
                  )}
                  <button
                    onClick={() => { setNetRegion(null); setCorrectionMode(false); setCorrectionPoints([]); }}
                    className="rounded-lg bg-slate-100 text-slate-700 px-3 py-2 text-xs font-semibold hover:bg-slate-200"
                  >
                    RIFIUTA
                  </button>
                </div>
                {correctionMode && (
                  <div className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-900 p-2">
                    Clicca due punti sul disegno per definire la nuova linea di separazione tra i due spessori.
                  </div>
                )}
              </div>
            )}
          </div>

          {Object.keys(confirmed).length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
              <h2 className="font-semibold text-slate-900 mb-3">Collegamento alle voci di computo</h2>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Stat label="Volume verificato" value={Object.values(confirmed).reduce((s,r)=>s+Number(r.volume_m3||0),0).toFixed(3) + " m³"} />
                <Stat label="Casseri verificati" value={Object.values(confirmed).reduce((s,r)=>s+Number(r.formwork?.total_area_m2||0),0).toFixed(2) + " m²"} />
              </div>
              <div className="flex gap-2 mb-3">
                <input
                  value={workItemQuery}
                  onChange={(e) => setWorkItemQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") searchWorkItems(); }}
                  placeholder="Cerca codice o descrizione..."
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
                <button onClick={() => searchWorkItems()} disabled={workItemsLoading}
                  className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
                  {workItemsLoading ? "Cerco..." : "Cerca"}
                </button>
              </div>
              <div className="space-y-3 mb-3">
                <WorkItemSelect label="Voce volume" value={volumeWorkItemId} onChange={setVolumeWorkItemId} items={workItems} />
                <WorkItemSelect label="Voce casseforme" value={formworkWorkItemId} onChange={setFormworkWorkItemId} items={workItems} />
              </div>
              <button onClick={linkBenchmarks} disabled={linking || (!volumeWorkItemId && !formworkWorkItemId)} className="w-full rounded-lg bg-slate-900 text-white px-3 py-2 text-sm font-semibold disabled:bg-slate-400">
                {linking ? "Collegamento..." : "Collega quantità verificate al computo"}
              </button>
            </div>
          )}

          {regionsForViewport.some((r) => r.selection_state === "GEOMETRY_AMBIGUOUS") && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <b>Revisione necessaria.</b> Il motore ha trovato geometrie incompatibili con l'assegnazione automatica degli spessori. Nessuna quantità viene approvata finché la regione netta non è risolta.
              </div>
            </div>
          )}
          {regionsForViewport.length > 0 && !regionsForViewport.some((r) => r.selection_state === "GEOMETRY_AMBIGUOUS") && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900">Le proposte sono pronte per la revisione tecnica.</div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function WorkItemSelect({ label, value, onChange, items }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
        <option value="">— Non collegare —</option>
        {(items || []).map((item) => (
          <option key={item.id} value={item.id}>
            {(item.code || "Senza codice") + " · " + (item.description || "").slice(0, 70) + " · " + (item.quantity ?? "—") + " " + (item.unit || "")}
          </option>
        ))}
      </select>
      {value && (() => {
        const item = (items || []).find((x) => x.id === value);
        if (!item) return null;
        return <div className="text-[11px] text-slate-400 mt-1">{item.offer?.title || item.offer?.client || "Offerta"} · quantità computo {item.quantity ?? "—"} {item.unit || ""}</div>;
      })()}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-semibold text-slate-800 mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function NetRegionSvg({ result, edges = [], correctionMode, correctionPoints, onPoint }) {
  const outer = result?.outer;
  const regions = result?.regions || [];
  if (!regions.length) return null;
  const allPoints = regions.flatMap((r) => r.polygon || []);
  if (!outer?.bbox && !allPoints.length) return null;
  const bounds = outer?.bbox || [
    Math.min(...allPoints.map((p) => p[0])),
    Math.min(...allPoints.map((p) => p[1])),
    Math.max(...allPoints.map((p) => p[0])),
    Math.max(...allPoints.map((p) => p[1])),
  ];
  const [x0, y0, x1, y1] = bounds;
  const width = Math.max(1, x1 - x0);
  const height = Math.max(1, y1 - y0);
  const pad = Math.max(width, height) * 0.06;
  const viewBox = [x0 - pad, y0 - pad, width + 2 * pad, height + 2 * pad].join(" ");
  const pts = (poly) => (poly || []).map((p) => p.join(",")).join(" ");
  const click = (event) => {
    if (!correctionMode) return;
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const px = x0 - pad + ((event.clientX - rect.left) / rect.width) * (width + 2 * pad);
    const py = y0 - pad + ((event.clientY - rect.top) / rect.height) * (height + 2 * pad);
    onPoint([px, py]);
  };
  return (
    <svg viewBox={viewBox} onClick={click}
      className={"w-full h-64 rounded-xl border bg-slate-50 " + (correctionMode ? "cursor-crosshair border-amber-300" : "border-slate-200")}>
      {outer?.exterior && <polygon points={pts(outer.exterior)} fill="none" stroke="currentColor" strokeWidth={Math.max(width, height) / 220} className="text-slate-500" />}
      {regions.map((r) => (
        <polygon key={r.region_key || r.region_class} points={pts(r.polygon)}
          fill={Number(r.thickness_m || 0) === Math.min(...regions.map(x => Number(x.thickness_m || 0))) ? "rgba(14,165,233,.22)" : "rgba(249,115,22,.22)"}
          stroke={Number(r.thickness_m || 0) === Math.min(...regions.map(x => Number(x.thickness_m || 0))) ? "rgb(14,165,233)" : "rgb(249,115,22)"}
          strokeWidth={Math.max(width, height) / 180} />
      ))}
      {result.split?.segment && (
        <line x1={result.split.segment[0]} y1={result.split.segment[1]} x2={result.split.segment[2]} y2={result.split.segment[3]}
          stroke="rgb(15,23,42)" strokeDasharray="8 5" strokeWidth={Math.max(width, height) / 120} />
      )}
      {edges.filter((e) => e.start && e.end && e.classification !== "INTERNAL_SPLIT").map((edge) => {
        const stroke = edge.classification === "FREE_EDGE"
          ? "rgb(22,163,74)"
          : (edge.classification === "STEP_P40_P60" || edge.classification === "STEP_THICKNESS_CHANGE")
            ? "rgb(124,58,237)"
            : "rgb(220,38,38)";
        return (
          <line key={edge.edge_id} x1={edge.start[0]} y1={edge.start[1]} x2={edge.end[0]} y2={edge.end[1]}
            stroke={stroke} strokeWidth={Math.max(width, height) / 90} strokeLinecap="round" />
        );
      })}
      {correctionPoints.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={Math.max(width, height) / 45} fill="rgb(217,119,6)" />
      ))}
    </svg>
  );
}
